/**
 * VdiErrorsSection
 * Shows VDI transaction error records in a modern compact table card.
 * Only rendered when errors exist.
 */
import React from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import type { IVDITransactionErrorProps } from '@/utils/types/vdiTransaction';
import { getDateTime } from '@/utils/dateTime';
import { Text } from '@/components/atoms/AppText';
import { IconBadge } from '@/components/atoms/IconBadge';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';

const HEADER_COLS = ['Date', 'Error Code', 'Status Code', 'Object Name', 'Message'];

export interface VdiErrorsSectionProps {
  errors: IVDITransactionErrorProps[];
  testId?: string;
}

export const VdiErrorsSection = ({ errors, testId }: VdiErrorsSectionProps) => {
  if (!errors?.length) return null;

  return (
    <SectionCard
      testId={testId}
      title={ORGANISM_PROPS.VDI_ERRORS.SECTION_TITLE}
      icon={
        <IconBadge
          icon={<FiAlertTriangle className="h-3 w-3" />}
          size="sm"
          variant="custom"
          customStyle={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)', color: 'white' }}
        />
      }
      badge={errors.length}
      className="border-red-200 dark:border-red-800/50"
      headerClassName="bg-[#E75065] dark:bg-[#c03d50] [&_p]:!text-white [&_span]:!text-white"
      iconVariant="custom"
    >
      <div className="overflow-x-auto -mx-4 sm:-mx-5 -mb-4 sm:-mb-5">
        {/* Table header */}
        <div className="grid grid-cols-[1.5fr_1fr_1fr_1.5fr_3fr] gap-2 px-4 py-2.5 bg-red-100 dark:bg-red-900/25 border-b border-red-200 dark:border-red-800/40">
          {HEADER_COLS.map((h) => (
            <Text
              as="span"
              key={h}
              size="xs"
              weight="bold"
              color="danger"
              tracking="widest"
              className="uppercase"
            >
              {h}
            </Text>
          ))}
        </div>

        {/* Rows */}
        <div className={errors.length > 3 ? 'overflow-y-auto max-h-[260px]' : ''}>
          {errors.map((err, i) => (
            <div
              key={`err-${err.vdiTransactionId}-${i}`}
              data-testid={`vdi-transaction-error-record-${i}`}
              className={[
                'grid grid-cols-[1.5fr_1fr_1fr_1.5fr_3fr] gap-2 px-4 py-3',
                'border-l-2 border-l-red-300 dark:border-l-red-600',
                'border-b border-red-100/80 dark:border-red-900/20 last:border-b-0',
                i % 2 === 0
                  ? 'bg-red-50/30 dark:bg-red-950/20'
                  : 'bg-white dark:bg-transparent',
                'hover:bg-red-50/60 dark:hover:bg-red-900/15 transition-colors',
              ].join(' ')}
            >
              {/* Date */}
              <Text as="span" size="md" color="muted" className="whitespace-nowrap self-start">
                {err.errorDtm ? getDateTime(err.errorDtm) : '—'}
              </Text>

              {/* Error code */}
              <span className="inline-flex items-center self-start px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 w-fit">
                {err.errorCode ?? '—'}
              </span>

              {/* Status code */}
              <Text as="span" size="md" weight="semibold" className="text-red-600 dark:text-red-400 self-start">
                {err.statusCode ?? '—'}
              </Text>

              {/* Object name */}
              <Text
                as="span"
                size="md"
                color="soft"
                className="truncate self-start"
                title={err.objectName}
              >
                {err.objectName ?? '—'}
              </Text>

              {/* Message — most important, largest visual weight */}
              <Text
                as="span"
                size="md"
                weight="medium"
                className="break-words text-red-700 dark:text-red-300 self-start"
              >
                {err.statusMessage ?? '—'}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </SectionCard>
  );
};
